import React, {FormEvent} from 'react';
import clsx from 'clsx';

interface GuestSendOtpModalProps {
  open: boolean;
  senderEmail: string;
  otpCode: string;
  onOtpChange: (value: string) => void;
  error: string;
  isSubmitting: boolean;
  isResending: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onResend: () => void;
}

export function GuestSendOtpModal({
  open,
  senderEmail,
  otpCode,
  onOtpChange,
  error,
  isSubmitting,
  isResending,
  onClose,
  onSubmit,
  onResend,
}: GuestSendOtpModalProps) {
  if (!open) return null;

  const canSubmit = otpCode.trim().length === 6 && !isSubmitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) onSubmit();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(83,83,83,0.35)] px-[16px]">
      <div className="relative w-full max-w-[520px] rounded-[32px] border-2 border-divider bg-paper px-[28px] py-[32px] shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[-12px] top-[-12px] flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#d8562a] text-[18px] text-white"
          aria-label="Close"
        >
          ×
        </button>

        <h2 className="text-center text-[22px] font-bold text-[#D8562A]">
          Verify your email
        </h2>
        <p className="mt-[12px] text-center text-[16px] font-bold text-[#535353]">
          We sent a code to <span className="text-main">{senderEmail}</span>.
          Enter it below to send your files.
        </p>

        <form onSubmit={handleSubmit} className="mt-[24px]">
          <div className="flex items-center gap-[12px]">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpCode}
              onChange={e =>
                onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="Password"
              className="h-[60px] flex-1 rounded-[20px] bg-[#F1F1F1] px-[20px] text-[17px] text-main placeholder:text-muted outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="submit"
              disabled={!canSubmit}
              className={clsx(
                'flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[20px] text-white transition-opacity',
                canSubmit
                  ? 'bg-[#2B7E20] hover:opacity-90'
                  : 'cursor-not-allowed bg-[#2B7E20]/40',
              )}
              aria-label="Verify and send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          {error && (
            <p className="mt-[12px] text-center text-[13px] font-medium text-[#E34444]">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={onResend}
            disabled={isResending || isSubmitting}
            className="mx-auto mt-[16px] block text-[14px] font-medium text-primary disabled:opacity-50"
          >
            {isResending ? 'Resending…' : 'Resend code'}
          </button>
        </form>

        <p
          className={clsx(
            'mt-[20px] text-center text-[14px] font-medium',
            canSubmit ? 'text-[#2B7E20]' : 'text-muted',
          )}
        >
          Verify and send
        </p>
      </div>
    </div>
  );
}
